import type { PerfectDrawProject } from "../projects/types";
import type { PersistedDocumentState } from "../comic/comicTypes";
import type { StoredRasterLayerBlob } from "../layers/layerTypes";
import { validatePersistedDocumentState } from "../comic/comicValidation";
import {
  applyDocumentStateToProject,
  migrateProject,
  migratePersistedDocumentState,
  projectDocumentState,
} from "../projects/projectMigration";
import {
  ARTWORK_STORE_NAME,
  openDatabase,
  PROJECT_STORE_NAME,
  requestToPromise,
  transactionToPromise,
} from "../storage/db";

export const ARTWORK_SCHEMA_VERSION = 3;

export interface StoredArtworkRecord {
  projectId: string;
  schemaVersion: number;
  revision: number;
  sourceGeneration: number;
  savedAt: string;
  mimeType: "image/png";
  currentLayers?: StoredRasterLayerBlob[];
  previousLayers?: StoredRasterLayerBlob[];
  /** Present only on legacy schema 1/2 records. */
  currentBlob?: Blob;
  previousBlob?: Blob;
  currentDocumentState?: PersistedDocumentState;
  previousDocumentState?: PersistedDocumentState;
}

export interface AtomicArtworkSave {
  projectId: string;
  sourceGeneration: number;
  savedAt: string;
  currentLayers?: StoredRasterLayerBlob[];
  /** Compatibility input used by migration tests and legacy imports. */
  currentBlob?: Blob;
  thumbnailBlob: Blob;
  currentDocumentState?: PersistedDocumentState;
  preservePreviousBlob?: boolean;
}

export class StaleArtworkSaveError extends Error {
  public constructor() {
    super("A newer artwork generation is already stored.");
    this.name = "StaleArtworkSaveError";
  }
}

function validateSaveBlobs(thumbnailBlob: Blob): void {
  if (thumbnailBlob.size === 0 || !thumbnailBlob.type.startsWith("image/")) {
    throw new Error("The thumbnail encoder did not produce a valid image Blob.");
  }
}

function validateLayerRecords(
  records: ReadonlyArray<StoredRasterLayerBlob>,
  state: PersistedDocumentState,
): StoredRasterLayerBlob[] {
  const expected = state.layers.filter((layer) => layer.type === "raster").map((layer) => layer.id);
  if (records.length !== expected.length) throw new Error("Raster layer save is incomplete.");
  const byId = new Map<string, StoredRasterLayerBlob>();
  for (const record of records) {
    if (byId.has(record.layerId) || record.mimeType !== "image/png") {
      throw new Error("Raster layer save is invalid.");
    }
    if (
      !record.blank &&
      (!record.blob || record.blob.size === 0 || record.blob.type !== "image/png")
    ) {
      throw new Error("Raster layer PNG is invalid.");
    }
    byId.set(record.layerId, {
      layerId: record.layerId,
      mimeType: "image/png",
      blank: record.blank,
      blob: record.blank ? undefined : record.blob,
    });
  }
  return expected.map((id) => {
    const record = byId.get(id);
    if (!record) throw new Error("Raster layer save is missing a layer.");
    return record;
  });
}

function currentLayersFromInput(
  input: AtomicArtworkSave,
  state: PersistedDocumentState,
): StoredRasterLayerBlob[] {
  if (input.currentLayers) return validateLayerRecords(input.currentLayers, state);
  const first = state.layers.find((layer) => layer.type === "raster");
  if (!first || !input.currentBlob) throw new Error("Artwork layers are missing.");
  return validateLayerRecords(
    state.layers
      .filter((layer) => layer.type === "raster")
      .map((layer) => ({
        layerId: layer.id,
        mimeType: "image/png" as const,
        blank: layer.id !== first.id,
        blob: layer.id === first.id ? input.currentBlob : undefined,
      })),
    state,
  );
}

export async function getArtworkRecord(
  projectId: string,
): Promise<StoredArtworkRecord | undefined> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(ARTWORK_STORE_NAME, "readonly");
    const record = (await requestToPromise(
      transaction.objectStore(ARTWORK_STORE_NAME).get(projectId),
    )) as StoredArtworkRecord | undefined;
    await transactionToPromise(transaction);
    return record;
  } finally {
    database.close();
  }
}

export async function saveArtworkAtomically(
  input: AtomicArtworkSave,
): Promise<StoredArtworkRecord> {
  validateSaveBlobs(input.thumbnailBlob);
  const database = await openDatabase();
  try {
    const transaction = database.transaction([PROJECT_STORE_NAME, ARTWORK_STORE_NAME], "readwrite");
    const projectStore = transaction.objectStore(PROJECT_STORE_NAME);
    const artworkStore = transaction.objectStore(ARTWORK_STORE_NAME);
    const rawProject = (await requestToPromise(projectStore.get(input.projectId))) as
      PerfectDrawProject | undefined;
    const previousRecord = (await requestToPromise(artworkStore.get(input.projectId))) as
      StoredArtworkRecord | undefined;

    if (!rawProject) {
      transaction.abort();
      throw new Error("The project no longer exists.");
    }
    if (previousRecord && input.sourceGeneration <= previousRecord.sourceGeneration) {
      transaction.abort();
      throw new StaleArtworkSaveError();
    }

    const project = migrateProject(rawProject).project;
    const currentDocumentState = validatePersistedDocumentState(
      input.currentDocumentState ?? projectDocumentState(project),
      project,
    );
    const currentLayers = currentLayersFromInput(input, currentDocumentState);
    const previousCurrentState = input.preservePreviousBlob
      ? undefined
      : previousRecord?.currentDocumentState
        ? migratePersistedDocumentState(previousRecord.currentDocumentState, project)
        : projectDocumentState(project);

    const artwork: StoredArtworkRecord = {
      projectId: input.projectId,
      schemaVersion: ARTWORK_SCHEMA_VERSION,
      revision: (previousRecord?.revision ?? 0) + 1,
      sourceGeneration: input.sourceGeneration,
      savedAt: input.savedAt,
      mimeType: "image/png",
      currentLayers,
      currentBlob: input.currentBlob,
      previousBlob: input.preservePreviousBlob
        ? previousRecord?.previousBlob
        : previousRecord?.currentBlob,
      previousLayers: input.preservePreviousBlob
        ? previousRecord?.previousLayers
        : previousRecord?.currentLayers,
      currentDocumentState,
      previousDocumentState: input.preservePreviousBlob
        ? (previousRecord?.previousDocumentState ??
          (previousRecord && previousRecord.schemaVersion < 2
            ? projectDocumentState(project)
            : undefined))
        : previousRecord
          ? previousCurrentState
          : undefined,
    };
    const updatedProject: PerfectDrawProject = {
      ...applyDocumentStateToProject(project, currentDocumentState),
      updatedAt: input.savedAt,
      thumbnailBlob: input.thumbnailBlob,
    };

    projectStore.put(updatedProject);
    artworkStore.put(artwork);
    await transactionToPromise(transaction);
    return artwork;
  } finally {
    database.close();
  }
}

export async function importProjectAtomically(
  project: PerfectDrawProject,
  artwork: Blob | ReadonlyArray<StoredRasterLayerBlob>,
  thumbnailBlob: Blob,
  documentState: PersistedDocumentState = projectDocumentState(project),
): Promise<StoredArtworkRecord> {
  validateSaveBlobs(thumbnailBlob);
  const database = await openDatabase();
  try {
    const transaction = database.transaction([PROJECT_STORE_NAME, ARTWORK_STORE_NAME], "readwrite");
    const projectStore = transaction.objectStore(PROJECT_STORE_NAME);
    const artworkStore = transaction.objectStore(ARTWORK_STORE_NAME);
    const existing = (await requestToPromise(projectStore.get(project.id))) as
      PerfectDrawProject | undefined;
    if (existing !== undefined) {
      transaction.abort();
      throw new Error("The imported project ID already exists.");
    }

    const migratedProject = migrateProject(project).project;
    const validatedState = validatePersistedDocumentState(documentState, migratedProject);
    const currentLayers = Array.isArray(artwork)
      ? validateLayerRecords(artwork, validatedState)
      : currentLayersFromInput(
          {
            projectId: project.id,
            sourceGeneration: 0,
            savedAt: project.updatedAt,
            currentBlob: artwork as Blob,
            thumbnailBlob,
            currentDocumentState: validatedState,
          },
          validatedState,
        );
    const record: StoredArtworkRecord = {
      projectId: project.id,
      schemaVersion: ARTWORK_SCHEMA_VERSION,
      revision: 1,
      sourceGeneration: 0,
      savedAt: project.updatedAt,
      mimeType: "image/png",
      currentLayers,
      currentDocumentState: validatedState,
    };
    projectStore.add({
      ...applyDocumentStateToProject(migratedProject, validatedState),
      thumbnailBlob,
    });
    artworkStore.add(record);
    await transactionToPromise(transaction);
    return record;
  } finally {
    database.close();
  }
}
